import { Category } from '../models/category';
import { Filter, FilterValue } from '../models/filter';
import { Offer } from '../models/offer';
import { OfferDefinition } from '../models/offer-definition';
import { AppPlatform } from '../models/platform';
import { CategoryUtils } from './category.utils';

export class OfferUtils {
    /**
     * Merge serialized filterValueEntities from offer/variant definitions into category filters
     * so display and edit flows can resolve labels for paginated choice filters.
     */
    static hydrateFilters(offer: Offer, filters: Filter[]): void {
        const merge = (def: OfferDefinition) => {
            if (!def.filterValueEntities?.length) {
                return;
            }
            const filter = filters.find((f) => f.id === def.filter);
            if (filter) {
                FilterValue.mergeIntoFilter(filter, def.filterValueEntities);
            }
        };
        for (const d of offer.definitions ?? []) {
            merge(d);
        }
        for (const v of offer.variants ?? []) {
            for (const d of v.definitions ?? []) {
                merge(d);
            }
        }
    }

    static countVariants(values: string[][]): number {
        if (!values || values.length === 0) {
            return 0;
        }

        // Values can be an object, so we need to get the values
        if (values instanceof Object) {
            values = Object.values(values);
        }

        let totalVariants = 0;
        for (const variant of values) {
            const variantLength = variant?.length || 0;
            totalVariants = variantLength !== 0 && totalVariants === 0 ? variantLength : totalVariants * variantLength;
        }

        return totalVariants;
    }

    static isSecondHand(platform: AppPlatform, category: Category): boolean {
        switch (platform) {
            case 'gazette':
                return CategoryUtils.findProperty(category, 'shippable');
            case 'marketplace':
            case 'wholesale':
            case 'motors':
            case 'estate':
                return false;
            default: {
                const exhaustive: never = platform;
                return exhaustive;
            }
        }
    }

    static canHaveVariants(platform: AppPlatform): boolean {
        switch (platform) {
            case 'marketplace':
            case 'wholesale':
                return true;
            case 'gazette':
            case 'motors':
            case 'estate':
                return false;
            default: {
                const exhaustive: never = platform;
                return exhaustive;
            }
        }
    }

    /**
     * Get the weight of a filter for column distribution
     * Multi-choice filters with multiple selected values have more weight
     * @param filter The filter to get weight for
     * @param definitions The actual selected definitions for this filter
     * @returns The weight of the filter
     */
    static getFilterWeight(filter: Filter, definitions: OfferDefinition[]): number {
        // Multi-choice filters with multiple selected values should count as multiple filters
        // Each selected value counts as 1 unit of weight
        if (filter.type === 'multi_choice' && definitions?.length > 0) {
            // Find matching definition for this filter
            const matchingDefinition = definitions.find((definition) => definition.filter === filter.id);
            if (matchingDefinition) {
                return matchingDefinition.filterValues?.length || 0;
            }
        }
        return 1;
    }

    /**
     * Distribute filters into columns based on their weight
     * @param filters Array of filter with definitions
     * @param columnCount Number of columns to distribute into
     * @returns Array of column objects containing filters
     */
    static distributeFiltersToColumns(
        filters: { filter: Filter; definitions: OfferDefinition[] }[],
        columnCount: number,
    ): { details: { filter: Filter; definitions: OfferDefinition[] }[] }[] {
        // Initialize columns with tracking of original indices
        const columns: { filter: Filter; definitions: OfferDefinition[]; originalIndex: number }[][] = [];
        const columnWeights: number[] = [];

        for (let i = 0; i < columnCount; i++) {
            columns.push([]);
            columnWeights.push(0);
        }

        // Add original index to each filter for later sorting
        const filtersWithIndex = filters.map((item, index) => ({ ...item, originalIndex: index }));

        // Sort filters by weight in descending order to place heavier filters first
        // This helps achieve better balance by avoiding situations where heavy filters
        // end up clustered in one column
        const sortedFilters = [...filtersWithIndex].sort((a, b) => {
            const weightA = OfferUtils.getFilterWeight(a.filter, a.definitions);
            const weightB = OfferUtils.getFilterWeight(b.filter, b.definitions);
            return weightB - weightA;
        });

        // Distribute filters to columns based on weight
        for (const item of sortedFilters) {
            const weight = OfferUtils.getFilterWeight(item.filter, item.definitions);

            // Find the column with the least weight
            const minWeight = Math.min(...columnWeights);
            const minIndex = columnWeights.indexOf(minWeight);

            // Add the filter to the column with least weight
            columns[minIndex].push(item);
            columnWeights[minIndex] += weight;
        }

        // Sort each column by original index to maintain filter order
        for (const column of columns) {
            column.sort((a, b) => a.originalIndex - b.originalIndex);
        }

        // Convert to the expected format, removing the originalIndex
        return columns.map((col) => ({
            details: col.map(({ filter, definitions }) => ({ filter, definitions })),
        }));
    }
}
